import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, Button, Input } from '@/components/ui'
import { apiClient, ApiError } from '@/services/apiClient'
import { useDeliveryOptions, type ServiceOptions } from '@/hooks/useDeliveryOptions'
import type {
  CredentialServiceSchema, CredentialFieldSchema, CredentialTestResult,
} from '@/types/credentialSchemas'
import type { IntegrationOverviewService } from '@/types/integrationsOverview'
import {
  buildEntryBody, describeChange, withDraft,
  type ConfigDrafts, type SecretsDrafts,
} from './dirtyTracking'
import { VehicleSelect, SpecialRequestChecklist } from './DynamicOptionFields'
import type { SetEntryDetail } from './IntegrationDrawer'

export interface EditServicePanelProps {
  row: IntegrationOverviewService
  schema: CredentialServiceSchema
  mode: 'edit' | 'create'
  setId: string
  setName: string
  assignedUserCount: number | null
  entry: SetEntryDetail | null
  onSaved: (changedCount: number) => void
  onCancel: () => void
  onRemoved: () => void
}

export function EditServicePanel({
  row, schema, mode, setId, setName, assignedUserCount, entry,
  onSaved, onCancel, onRemoved,
}: EditServicePanelProps) {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<ConfigDrafts>({})
  const [secretDraft, setSecretDraft] = useState('')
  const [secretsDrafts, setSecretsDrafts] = useState<SecretsDrafts>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  // Paste-triggers-probe (§4.6): a candidate Test-connection fired on paste
  // only — never on typing. Transient, never persisted; the persisted
  // verdict (read-view Test connection) is untouched by this state.
  const [candidate, setCandidate] = useState<
    { status: 'idle' } | { status: 'checking' } | { status: 'done'; ok: boolean; detail: string }
  >({ status: 'idle' })
  const pasteFlag = useRef(false)
  // Bumped on every new probe AND on the empty-field reset, so an in-flight
  // request whose token has been superseded is orphaned: its resolution
  // must not overwrite a newer probe's result (out-of-order double-paste)
  // or resurrect a result for a key the user already cleared.
  const candidateToken = useRef(0)

  const configFields = schema.fields.filter((f) => f.storage === 'config')
  const secretField = schema.fields.find((f) => f.storage === 'secret' && f.key === 'api_key')
  const secondaryFields = schema.fields.filter((f) => f.storage === 'secret' && f.key !== 'api_key')
  const storedConfig = entry?.config ?? null
  const storedSecretsSet = entry?.secrets_set ?? {}
  const { body, changedFields, changedCount } = buildEntryBody({
    service: row.service,
    name: entry?.name ?? schema.label,
    configFields, storedConfig, drafts, secretDraft,
    secondaryFields, secretsDrafts, storedSecretsSet,
  })
  const canSave = mode === 'create' ? !!body.api_key : changedCount > 0

  // Dynamic-options fields (Lalamove vehicle/special-requests) load live from
  // a backend endpoint. Options only load when a stored entry exists — the
  // endpoint reads stored keys — so create mode falls back to the plain-text
  // inputs below (documented wart, matches the legacy modal's behavior).
  const hasDynamicFields = configFields.some(
    (f) => f.dynamic_options === 'lalamove-delivery-options',
  )
  const deliveryOptions = useDeliveryOptions(setId, hasDynamicFields && entry !== null)
  const deliveryServices: ServiceOptions[] =
    deliveryOptions.data?.cities.flatMap((c) => c.services) ?? []
  const optionsFailed = hasDynamicFields && entry !== null && deliveryOptions.isError
  const optionsEmpty = hasDynamicFields && entry !== null
    && deliveryOptions.isSuccess && deliveryServices.length === 0

  const stage = (field: CredentialFieldSchema, value: string | null) =>
    setDrafts((d) => withDraft(d, field, storedConfig?.[field.key], value))

  // ApiError.message is the raw HTTP status line ("POST ... failed: ...");
  // the actionable reason is the server's `detail` (FastAPI's {detail: ...}
  // envelope), stashed separately — prefer that when present.
  function errorMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError && typeof err.detail === 'string') return err.detail
    if (err instanceof Error) return err.message
    return fallback
  }

  // Fired on paste only (see the secret input's onPaste/onChange wiring
  // below). Probes the pasted candidate key against the service's live
  // endpoint via the same Test-connection route, but with a candidate body
  // — the backend overlays it on stored creds WITHOUT persisting a verdict
  // (Task 2). Non-null config drafts ride along so a candidate key is
  // checked against any staged config changes too.
  async function checkCandidate(key: string) {
    const trimmed = key.trim()
    if (!row.probe_available || !trimmed) return
    const token = (candidateToken.current += 1)
    setCandidate({ status: 'checking' })
    try {
      const draftConfig = Object.fromEntries(
        Object.entries(drafts).filter(([, v]) => v !== null),
      ) as Record<string, string>
      const payload: Record<string, unknown> = { api_key: trimmed }
      if (Object.keys(draftConfig).length > 0) payload.config = draftConfig
      const r = await apiClient.post<CredentialTestResult>(
        `/api/admin/credential-sets/${setId}/entries/${row.service}/test`, payload,
      )
      if (candidateToken.current !== token) return // superseded — orphaned
      setCandidate({ status: 'done', ok: r.ok, detail: r.detail })
    } catch (err) {
      if (candidateToken.current !== token) return // superseded — orphaned
      setCandidate({ status: 'done', ok: false, detail: errorMessage(err, 'Check failed') })
    }
  }

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: ['integrations-overview'] })
    await queryClient.invalidateQueries({ queryKey: ['credential-set-detail', setId] })
    // Sets-list has its own 5-min app-wide staleTime; without this, the
    // create picker / "+ Add to another set" affordance can keep offering
    // (or keep hiding) a set based on stale membership after a save/remove.
    await queryClient.invalidateQueries({ queryKey: ['credential-sets-summary'] })
  }

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      await apiClient.post(`/api/admin/credential-sets/${setId}/entries`, body)
      await invalidate()
      onSaved(mode === 'create' ? changedCount || 1 : changedCount)
    } catch (err) {
      setSaveError(errorMessage(err, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  async function removeEntry() {
    setSaving(true)
    setSaveError(null)
    try {
      const res = await apiClient.fetch(
        `/api/admin/credential-sets/${setId}/entries/${row.service}`,
        { method: 'DELETE' },
      )
      if (!res.ok) throw new Error('Failed to remove key')
      await invalidate()
      onRemoved()
    } catch (err) {
      setSaveError(errorMessage(err, 'Failed to remove key'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4" data-testid="edit-panel">
      {saveError && (
        <Alert variant="destructive"><AlertDescription>{saveError}</AlertDescription></Alert>
      )}

      {/* Secret */}
      {secretField && (
        <div>
          <Input
            id={`edit-${row.service}-${secretField.key}`}
            type="password"
            label={secretField.label}
            value={secretDraft}
            onPaste={(e) => {
              // Only arm the flag when the paste actually carries data — an
              // empty/blocked paste fires onPaste but no onChange, and an
              // unconditional flag would leak onto the next typed
              // character as a spurious 1-char candidate probe.
              if (e.clipboardData.getData('text').trim() !== '') pasteFlag.current = true
            }}
            onChange={(e) => {
              setSecretDraft(e.target.value)
              if (pasteFlag.current) {
                pasteFlag.current = false
                void checkCandidate(e.target.value)
              } else if (e.target.value === '') {
                candidateToken.current += 1 // orphan any in-flight probe
                setCandidate({ status: 'idle' })
              }
            }}
          />
          <p data-testid="secret-keep-state" className="mt-1 text-xs">
            {mode === 'create' || !entry?.has_key ? (
              <span className="text-muted-foreground">No key stored yet — paste one to add it</span>
            ) : secretDraft.trim() ? (
              <span className="text-warning">Will replace the existing key on save</span>
            ) : (
              <span className="text-muted-foreground">
                Keeping existing key — leave blank to keep it
              </span>
            )}
          </p>
          {candidate.status !== 'idle' && (
            <p data-testid="candidate-probe" className="mt-1 text-xs" aria-live="polite">
              {candidate.status === 'checking' ? (
                <span className="text-muted-foreground">Checking pasted key…</span>
              ) : candidate.ok ? (
                <span className="text-success">Key check passed — remember to save.</span>
              ) : (
                <span className="text-destructive">Key check failed: {candidate.detail}</span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Secondary secrets (e.g. Stripe's webhook signing secret) — one
          masked input per field, independent of the canonical api_key
          input above. No paste-probe wiring here: probing is scoped to the
          canonical secret only. */}
      {secondaryFields.map((f) => {
        const stored = storedSecretsSet[f.key] ?? false
        const draft = secretsDrafts[f.key]
        const value = typeof draft === 'string' ? draft : ''
        const prefixBad = !!(
          f.validate_prefix && value.trim() !== '' && !value.trim().startsWith(f.validate_prefix)
        )
        return (
          <div key={f.key}>
            <Input
              id={`edit-${row.service}-${f.key}`}
              type="password"
              label={f.label}
              value={value}
              placeholder={f.placeholder}
              onChange={(e) =>
                setSecretsDrafts((prev) => ({ ...prev, [f.key]: e.target.value }))
              }
            />
            {prefixBad && (
              <p className="mt-1 text-xs text-warning">
                {f.label} should start with &quot;{f.validate_prefix}&quot; — check you
                copied the right value.
              </p>
            )}
            <p data-testid={`secret-state-${f.key}`} className="mt-1 text-xs">
              {draft === null ? (
                <span className="text-warning">will be cleared</span>
              ) : value.trim() ? (
                <span className="text-warning">
                  {stored ? 'will be replaced' : 'will be set'}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {stored ? 'configured — leave blank to keep' : 'not set'}
                </span>
              )}
            </p>
            {stored && draft !== null && (
              <Button
                size="sm" variant="ghost" aria-label={`Clear ${f.label}`}
                onClick={() => setSecretsDrafts((prev) => ({ ...prev, [f.key]: null }))}
              >
                Clear
              </Button>
            )}
          </div>
        )
      })}

      {/* Dynamic-option fields must never be silently indistinguishable from
          the plain-text fallback: loading gets a skeleton, failure and
          empty-catalog get a visible notice above the fields. */}
      {optionsFailed && (
        <Alert variant="warning">
          <AlertDescription>
            Couldn&apos;t load options from Lalamove (
            {deliveryOptions.error instanceof Error
              ? deliveryOptions.error.message
              : 'unknown error'}
            ) — you can type values manually below.{' '}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void deliveryOptions.refetch()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {optionsEmpty && (
        <Alert variant="warning">
          <AlertDescription>
            Lalamove returned no delivery options for the configured
            market — you can type values manually below.
          </AlertDescription>
        </Alert>
      )}

      {/* Config fields */}
      <div className="space-y-3">
        {configFields.map((f) => {
          const stored = storedConfig?.[f.key]
          const draft = drafts[f.key]
          const fieldId = `edit-${row.service}-${f.key}`

          // Dynamic-options fields (Lalamove vehicle/special-requests): live
          // pickers when the catalog is loaded, a disabled loading skeleton
          // while it's in flight. Fetch failure/empty-catalog/create-mode
          // fall through to the plain enum/text branches below (the "type
          // values manually below" fallback).
          if (f.dynamic_options === 'lalamove-delivery-options' && entry !== null
              && deliveryOptions.isLoading) {
            return (
              <div key={f.key} data-testid={`edit-field-${f.key}`}>
                <label htmlFor={fieldId} className="block text-sm font-medium text-foreground mb-1">
                  {f.label}
                </label>
                <select id={fieldId} disabled aria-busy="true"
                        className="w-full text-sm rounded-md border border-border bg-card px-3 py-2 text-muted-foreground">
                  <option>Loading options from Lalamove…</option>
                </select>
              </div>
            )
          }
          if (f.dynamic_options === 'lalamove-delivery-options' && deliveryServices.length > 0) {
            const vehicleField = configFields.find((x) => x.key === 'service_type')!
            const requestsField = configFields.find((x) => x.key === 'special_requests')!
            const vehicleRaw = (drafts.service_type ?? storedConfig?.service_type ?? '') as string
            const requestsRaw = (drafts.special_requests ?? storedConfig?.special_requests ?? '') as string
            const selectedRequests = requestsRaw.split(',').map((s) => s.trim()).filter(Boolean)
            // Blank vehicle = adapter default (HATCHBACK)
            const effectiveVehicle = vehicleRaw || 'HATCHBACK'
            if (f.type === 'select') {
              return (
                <VehicleSelect
                  key={f.key} fieldId={fieldId} label={f.label}
                  services={deliveryServices} value={vehicleRaw}
                  selectedRequests={selectedRequests}
                  onChange={(vehicle, pruned) => {
                    setDrafts((d) => withDraft(
                      withDraft(d, vehicleField, storedConfig?.service_type, vehicle),
                      requestsField, storedConfig?.special_requests, pruned.join(','),
                    ))
                  }}
                />
              )
            }
            if (f.type === 'multiselect') {
              return (
                <SpecialRequestChecklist
                  key={f.key} fieldId={fieldId} label={f.label}
                  service={deliveryServices.find((s) => s.key === effectiveVehicle)}
                  selected={selectedRequests}
                  onChange={(requests) => stage(f, requests.join(','))}
                />
              )
            }
          }

          if (draft === null) {
            return (
              <div key={f.key} data-testid={`edit-field-${f.key}`}
                   className="flex items-center justify-between rounded-md border border-dashed border-border px-3 py-2">
                <span className="text-sm text-muted-foreground italic">
                  {f.label}: {f.default_value
                    ? `default "${f.default_value}" will apply`
                    : 'will be unset'}
                </span>
                <Button size="sm" variant="ghost" aria-label={`Undo clear ${f.label}`}
                        onClick={() => stage(f, stored ?? '')}>
                  Undo
                </Button>
              </div>
            )
          }
          const value = draft === undefined ? (stored ?? '') : draft
          return (
            <div key={f.key} data-testid={`edit-field-${f.key}`} className="flex items-end gap-2">
              <div className="flex-1">
                {f.type === 'enum' && f.enum_values.length > 0 ? (
                  <div>
                    <label htmlFor={fieldId}
                           className="block text-sm font-medium text-foreground mb-1">
                      {f.label}
                    </label>
                    <select
                      id={fieldId}
                      value={value}
                      onChange={(e) => stage(f, e.target.value)}
                      className="w-full text-sm rounded-md border border-border bg-card px-3 py-2 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select…</option>
                      {f.enum_values.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                ) : (
                  <Input
                    id={fieldId}
                    type={f.type === 'number' ? 'number' : 'text'}
                    label={f.label}
                    placeholder={f.placeholder || ''}
                    value={value}
                    onChange={(e) => stage(f, e.target.value)}
                  />
                )}
              </div>
              {stored !== undefined && f.dynamic_options === '' && (
                <Button size="sm" variant="ghost" aria-label={`Clear ${f.label}`}
                        onClick={() => stage(f, null)}>
                  Clear
                </Button>
              )}
            </div>
          )
        })}
      </div>

      {/* Changed-fields summary */}
      {changedCount > 0 && (
        <ul data-testid="changed-summary"
            className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground space-y-0.5">
          {changedFields.map((f) => (
            <li key={f.key}>
              {f.storage === 'secret'
                ? `${f.label}: ${secretsDrafts[f.key] === null ? 'will be cleared' : 'will be replaced'}`
                : `${f.label}: ${describeChange(f, storedConfig?.[f.key], drafts[f.key] as string | null)}`}
            </li>
          ))}
          {body.api_key && <li>{secretField?.label ?? 'API Key'}: will be replaced</li>}
        </ul>
      )}

      {assignedUserCount != null && assignedUserCount > 0 && (
        <p className="text-sm text-info">
          Changes apply immediately to all {assignedUserCount} assigned user
          {assignedUserCount !== 1 ? 's' : ''}.
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={!canSave || saving} isLoading={saving}>
          {mode === 'create'
            ? `Add to "${setName}"`
            : `Update ${changedCount} changed field${changedCount === 1 ? '' : 's'}`}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        {mode === 'edit' && (
          confirmingRemove ? (
            <span className="ml-auto flex flex-col items-end gap-1">
              {secondaryFields.some((f) => storedSecretsSet[f.key]) && (
                <p className="text-xs text-warning">
                  {secondaryFields
                    .filter((f) => storedSecretsSet[f.key])
                    .map((f) => f.label)
                    .join(', ')}{' '}
                  will also be removed with this entry.
                </p>
              )}
              <span className="flex items-center gap-2">
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive/90"
                        onClick={() => void removeEntry()} disabled={saving}>
                  Confirm remove
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingRemove(false)}>
                  Keep
                </Button>
              </span>
            </span>
          ) : (
            <Button size="sm" variant="ghost"
                    className="ml-auto text-destructive hover:text-destructive/90"
                    onClick={() => setConfirmingRemove(true)} disabled={saving}>
              Remove key from this set
            </Button>
          )
        )}
      </div>
    </div>
  )
}
